import { useMemo, useState } from 'react';
import { GitBranch, RefreshCw, Loader2, Search, Lock } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { useBreadcrumbs } from '@/client/hooks/use-breadcrumbs';
import { useHasPermission } from '@/client/hooks/useHasPermission';
import { getApiErrorMessage } from '@/client/utils/errors';
import {
  useDeliveryGatesQuery,
  useDeliveryRoutingQuery,
} from '@/client/api/notifications-infra/notifications.query';
import {
  DELIVERY_BASE,
  DeliveryAccessDenied,
  DeliveryPageShell,
  GatesBanner,
  SourceBadge,
  formatProviderName,
  normalizeGates,
  routingCountry,
  routingRows,
} from '@/client/components/admin/NotificationsInfra/deliveryUi';

const DeliveryRoutingPage = () => {
  useBreadcrumbs([
    { label: 'Communications', href: `${DELIVERY_BASE}/delivery` },
    { label: 'Notifications' },
    { label: 'Routing' },
  ]);

  const viewPerm = useHasPermission('messaging.view');
  const canView = viewPerm.isSuperAdmin || viewPerm.hasPermission;
  const permLoading = viewPerm.isLoading;

  const routingQ = useDeliveryRoutingQuery(canView);
  const gatesQ = useDeliveryGatesQuery(canView);
  const [q, setQ] = useState('');

  const rows = useMemo(() => {
    const list = routingRows(routingQ.data);
    const term = q.trim().toLowerCase();
    if (!term) return list;
    return list.filter((row) => {
      const hay = [
        routingCountry(row),
        row.country_name,
        row.country,
        row.provider,
        row.source,
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return hay.includes(term);
    });
  }, [routingQ.data, q]);

  const editable = routingQ.data?.editable === true || rows.some((r) => r.editable === true);
  const anyEditableFlag = rows.some((r) => r.editable === true);
  const showEditors = editable || anyEditableFlag;

  if (permLoading) {
    return (
      <DeliveryPageShell loading icon={null} title="Routing" description="">
        {null}
      </DeliveryPageShell>
    );
  }

  if (!canView) {
    return (
      <div className="min-h-screen bg-gray-50 p-6">
        <div className="mx-auto max-w-screen-2xl">
          <DeliveryAccessDenied />
        </div>
      </div>
    );
  }

  return (
    <DeliveryPageShell
      icon={<GitBranch className="h-4 w-4" />}
      title="Routing & gates"
      description="Effective country → SMS provider, with the rule that decided it. Gates suppress silently before dispatch."
      actions={
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => {
            routingQ.refetch();
            gatesQ.refetch();
          }}
          disabled={routingQ.isFetching}
          className="gap-2"
        >
          {routingQ.isFetching ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <RefreshCw className="h-4 w-4" />
          )}
          Refresh
        </Button>
      }
    >
      <GatesBanner gates={normalizeGates(gatesQ.data)} loading={gatesQ.isLoading} />

      {!showEditors && (
        <div className="flex items-start gap-2 rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-700">
          <Lock className="mt-0.5 h-4 w-4 shrink-0" />
          Routing lives in code constants plus env vars and needs a redeploy. No editors until
          the API flips <code className="rounded bg-white px-1 text-xs">editable: true</code>.
        </div>
      )}

      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
        <input
          type="search"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Filter by country, provider, or source…"
          className="w-full rounded-md border border-gray-300 bg-white py-2 pl-9 pr-3 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
      </div>

      <div className="overflow-hidden rounded-xl border border-gray-100 bg-white shadow-sm">
        {routingQ.isLoading ? (
          <div className="space-y-2 p-4">
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-10 w-full" />
          </div>
        ) : routingQ.isError ? (
          <p className="p-8 text-center text-sm text-red-600">
            {getApiErrorMessage(routingQ.error, 'Failed to load routing.')}
          </p>
        ) : rows.length === 0 ? (
          <p className="p-8 text-center text-sm text-gray-500">No routing rows.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full text-left text-sm">
              <thead className="border-b bg-gray-50 text-xs uppercase text-gray-500">
                <tr>
                  <th className="px-4 py-3 font-medium">Country</th>
                  <th className="px-4 py-3 font-medium">Provider</th>
                  <th className="px-4 py-3 font-medium">Source</th>
                  <th className="px-4 py-3 font-medium">Flags</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {rows.map((row, i) => {
                  const cc = routingCountry(row);
                  return (
                    <tr key={`${cc}-${row.provider}-${i}`} className={row.blocked ? 'bg-red-50/40' : undefined}>
                      <td className="px-4 py-3">
                        <span className="font-mono text-xs font-semibold text-gray-900">{cc || '—'}</span>
                        {row.country_name || (row.country && row.country !== cc) ? (
                          <span className="ml-2 text-gray-600">{row.country_name || row.country}</span>
                        ) : null}
                      </td>
                      <td className="px-4 py-3 font-medium text-gray-900">
                        {row.provider ? formatProviderName(String(row.provider)) : '—'}
                      </td>
                      <td className="px-4 py-3">
                        <SourceBadge source={row.source} />
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex flex-wrap gap-1">
                          {row.blocked ? (
                            <span className="rounded-full bg-red-50 px-2 py-0.5 text-[11px] font-medium text-red-700">
                              Blocked
                            </span>
                          ) : null}
                          {row.dedicated_number ? (
                            <span className="rounded-full bg-sky-50 px-2 py-0.5 text-[11px] font-medium text-sky-800">
                              Dedicated number
                            </span>
                          ) : null}
                          {!row.blocked && !row.dedicated_number ? (
                            <span className="text-xs text-gray-400">—</span>
                          ) : null}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </DeliveryPageShell>
  );
};

export default DeliveryRoutingPage;
